import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Regex matching any character that is illegal in XML 1.0. */
const ILLEGAL_XML_CHARS = /[^\x09\x0A\x0D\x20-\uD7FF\uE000-\uFFFD\u{10000}-\u{10FFFF}]/u;

/**
 * Validator that rejects values containing characters illegal in XML 1.0.
 * Returns `{ illegalXmlChars: true }` when an illegal character is found, otherwise null.
 */
export const noIllegalXmlCharsValidator: ValidatorFn =
  (control: AbstractControl): ValidationErrors | null => {
    if (control.value && ILLEGAL_XML_CHARS.test(control.value)) {
      return { illegalXmlChars: true };
    }
    return null;
  };

/**
 * Scrolls the page to the given textarea, scrolls within it to the first
 * illegal XML character, and selects it so the user can see exactly what needs fixing.
 */
export function highlightIllegalXmlChars(textarea: HTMLTextAreaElement, value: string): void {
  const match = ILLEGAL_XML_CHARS.exec(value);
  if (match) {
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(match.index, match.index + match[0].length);
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20;
    const linesBeforeMatch = value.substring(0, match.index).split('\n').length - 1;
    textarea.scrollTop = linesBeforeMatch * lineHeight;
    textarea.scrollIntoView({ block: 'center' });
  }
}

/**
 * Scrolls within the textarea to the first illegal XML character without
 * stealing focus. Intended for blur handlers where re-focusing would trap the user.
 */
export function scrollToIllegalXmlChar(textarea: HTMLTextAreaElement, value: string): void {
  const match = ILLEGAL_XML_CHARS.exec(value);
  if (match) {
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 20;
    const linesBeforeMatch = value.substring(0, match.index).split('\n').length - 1;
    textarea.scrollTop = linesBeforeMatch * lineHeight;
  }
}
